import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon } from "lucide-react";
import Button from "../common/Button";
import Loader from "../common/Loader";
import { getPromoImage, setPromoImage, removePromoImage, uploadPromoImage } from "../../services/promoImageService";
import { color, radius } from "../../styles/theme";

/**
 * Promo Image — ported from mobile's create-promo-media screen
 * (src/Screens/create-promo-media, titled "Social Album Thumbnail" there). A
 * single account-level social-share preview image, not per-event — see the
 * approved Phase 6 plan for why this lives here instead of a per-event route.
 */
const PromoImageTab = () => {
  const { t } = useTranslation(["settings", "common"]);
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null); // { url, thumbnail }
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    getPromoImage()
      .then((res) => {
        const { promoImageUrl, thumbnailUrl } = res.data || {};
        if (promoImageUrl && thumbnailUrl) setImage({ url: promoImageUrl, thumbnail: thumbnailUrl });
      })
      .catch(() => toast.error(t("promoImageTab.toasts.loadFailed")))
      .finally(() => setLoading(false));
  }, []);

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadPromoImage(file);
      if (uploaded) setImage(uploaded);
      else toast.error(t("promoImageTab.toasts.uploadFailed"));
    } catch {
      toast.error(t("promoImageTab.toasts.uploadFailed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    if (!image?.url?.startsWith("https://")) {
      toast.error(t("promoImageTab.toasts.addImageFirst"));
      return;
    }
    setSaving(true);
    try {
      const res = await setPromoImage({ promoImageUrl: image.url, thumbnailUrl: image.thumbnail });
      toast.success(res.data?.message || t("promoImageTab.toasts.saved"));
    } catch (err) {
      toast.error(err.response?.data?.message || t("promoImageTab.toasts.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await removePromoImage();
      setImage(null);
      toast.success(t("promoImageTab.toasts.removed"));
    } catch (err) {
      toast.error(err.response?.data?.message || t("promoImageTab.toasts.removeFailed"));
    } finally {
      setRemoving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div style={s.wrap}>
      <p style={s.desc}>
        {t("promoImageTab.description")}
      </p>

      <div style={s.previewBox}>
        {image?.thumbnail ? (
          <img src={image.thumbnail} alt="" style={s.previewImg} />
        ) : (
          <div style={s.previewPlaceholder}>
            <ImageIcon size={26} strokeWidth={1.75} color={color.primary400} />
            <span>{t("promoImageTab.noImage")}</span>
          </div>
        )}
      </div>

      <label className="btn btn-secondary btn-sm" style={{ ...s.uploadBtn, ...(uploading ? { opacity: 0.5, pointerEvents: "none" } : {}) }}>
        {uploading ? t("promoImageTab.uploading") : image ? t("promoImageTab.replaceImage") : t("promoImageTab.addImage")}
        <input type="file" accept="image/*" style={{ display: "none" }} onChange={handlePick} disabled={uploading} />
      </label>

      <div style={s.actions}>
        <Button onClick={handleSave} disabled={saving || uploading || !image}>{saving ? t("promoImageTab.saving") : t("common:actions.save")}</Button>
        {image && (
          <Button variant="secondary" onClick={handleRemove} disabled={removing || saving}>
            {removing ? t("promoImageTab.removing") : t("common:actions.remove")}
          </Button>
        )}
      </div>
    </div>
  );
};

const s = {
  wrap: { maxWidth: 420 },
  desc: { fontSize: 12.5, color: color.textMuted, marginBottom: 18, lineHeight: 1.5 },
  previewBox: {
    width: "100%", height: 220, borderRadius: radius.md, background: color.primary50,
    display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", marginBottom: 16,
    border: `1.5px dashed ${color.primary100}`,
  },
  previewImg: { width: "100%", height: "100%", objectFit: "cover" },
  previewPlaceholder: { display: "flex", flexDirection: "column", alignItems: "center", gap: 8, fontSize: 12.5, color: color.primary400, fontWeight: 500 },
  uploadBtn: { display: "inline-flex", marginBottom: 16 },
  actions: { display: "flex", gap: 10 },
};

export default PromoImageTab;
